import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import {
  Box,
  RadioGroup,
  RadioItem,
  Spread,
  Text,
  Toggle,
} from 'braid-design-system';
import type { FormEvent } from 'react';
import { Fragment } from 'react/jsx-runtime';

import { isFeatureActive } from 'src/modules/features';
import { useTypedSelector } from 'src/reducers';
import {
  type UpdateNotificationCtpPreferencesMutation,
  type UpdateNotificationCtpPreferencesMutationVariables,
  type NotificationCtpPreferenceProductType,
  NotificationCtpUpdateReasonType,
  type NotificationCtpChannelType,
  type NotificationCtpFrequencyType,
  type NotificationCtpPreferencesInput,
  type NotificationCtpProduct,
  type NotificationCtpSubscription,
  type NotificationCtpCategory,
} from 'src/types/graphql';

import {
  getCTPPreferenceQL,
  updateCTPPreferenceQL,
} from '../../modules/graphql/notifications/preference.graphql';

import { trackOptionSelectionPressed, trackToggle } from './tracking/tealium';

interface SubscriptionToggleProps {
  product: NotificationCtpProduct;
  subscription: NotificationCtpSubscription;
  categoryId: NotificationCtpCategory['id'];
}

const SubscriptionToggle: React.FC<SubscriptionToggleProps> = ({
  product,
  subscription,
  categoryId,
}) => {
  const { channel, subscribed, frequency } = subscription;
  const { language } = useMelwaysInfo();
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();
  const locale = `${language}-${country}`;
  const onlyProductionOption = !isFeatureActive('show-hidden-preferences');

  const [updateCTPSubscriptions] = useMutation<
    UpdateNotificationCtpPreferencesMutation,
    UpdateNotificationCtpPreferencesMutationVariables
  >(updateCTPPreferenceQL, {
    refetchQueries: [getCTPPreferenceQL],
  });

  const updatePreference = async ({
    subscribe,
    frequencyValue = frequency?.value,
  }: {
    subscribe: boolean;
    frequencyValue?: string;
  }) => {
    const preferences: NotificationCtpPreferencesInput[] = [
      {
        id: product.id as NotificationCtpPreferenceProductType,
        notificationsChannel: [
          {
            channel: channel.id as NotificationCtpChannelType,
            frequency: frequencyValue as NotificationCtpFrequencyType,
            reason: NotificationCtpUpdateReasonType.PreferenceUi,
            subscribe,
          },
        ],
      },
    ];

    const { data } = await updateCTPSubscriptions({
      variables: {
        input: {
          preferences,
          zone,
          onlyProductionOption: Boolean(onlyProductionOption),
        },
        locale,
      },
      optimisticResponse({ input }) {
        const newSubscription = input.preferences[0].notificationsChannel[0];
        return {
          updateNotificationCtpPreferences: {
            __typename: 'UpdateNotificationCtpPreferencesSuccess',
            success: [
              {
                __typename: 'NotificationCTPPreferenceGroup',
                preferences: [
                  {
                    product: {
                      id: input.preferences[0].id,
                      __typename: 'NotificationCTPProduct',
                    },
                    subscriptions: [
                      {
                        __typename: 'NotificationCTPSubscription',
                        id: subscription.id,
                        subscribed: newSubscription.subscribe,
                        channel: {
                          label: channel.label,
                          id: channel.id,
                          __typename: 'NotificationCTPChannel',
                        },
                        frequency:
                          newSubscription.frequency && frequency
                            ? {
                                value: newSubscription.frequency,
                                options: frequency.options,
                                __typename: 'NotificationCTPFrequency',
                              }
                            : null,
                        token: subscription.token,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        };
      },
    });

    return data;
  };

  const onSubscriptionToggleChange = async (subscribe: boolean) => {
    const data = await updatePreference({
      subscribe,
    });

    if (
      data?.updateNotificationCtpPreferences?.__typename ===
      'UpdateNotificationCtpPreferencesSuccess'
    ) {
      trackToggle({
        product: product.id,
        channel: channel.id,
        category: categoryId,
        subscribed: subscribe,
        frequency: frequency?.value,
      });
    }
  };

  const onSubscriptionFrequencyChange = async (
    e: FormEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value;
    const data = await updatePreference({
      subscribe: true,
      frequencyValue: value,
    });
    if (
      data?.updateNotificationCtpPreferences?.__typename ===
      'UpdateNotificationCtpPreferencesSuccess'
    ) {
      trackOptionSelectionPressed({
        product: product.id,
        channel: channel.id,
        optionSelectionType: 'radio',
        optionSelected: value,
      });
    }
  };

  return (
    <Fragment>
      <Spread space="medium" alignY="center">
        <Text weight="strong">{channel.label}</Text>
        <Toggle
          align="justify"
          label=""
          on={subscribed}
          id={`${product.id}-${channel.id}`}
          onChange={onSubscriptionToggleChange}
        />
      </Spread>
      {subscribed && frequency ? (
        <Box paddingX="xsmall" marginTop="medium">
          <RadioGroup
            id={`${product.id}-frequency`}
            aria-label={product.label}
            value={frequency.value}
            onChange={onSubscriptionFrequencyChange}
          >
            {frequency.options.map((option) => (
              <RadioItem
                key={option.id}
                label={option.label}
                value={option.id}
                description={option.description}
              />
            ))}
          </RadioGroup>
        </Box>
      ) : null}
    </Fragment>
  );
};

export default SubscriptionToggle;
